import type { VNode } from "preact";
import { useCallback, useEffect, useState } from "preact/hooks";
import { SettingsPanel } from "./SettingsPanel";
import { ToastHost, useToasts } from "./Toast";
import { DonationBanner } from "./DonationBanner";
import { navigate } from "../lib/nav";
import {
  getPreferredInstance,
  setPreferredInstance,
  getRedirectX,
  setRedirectX,
  getCustomSource,
  setCustomSource,
} from "../settings";

/** Section ids the /settings deep links accept: /settings#tooltips etc.
 *  The sections carry matching id="set-<name>" attributes. */
const SECTIONS = ["redirect", "data-source", "preferred", "priority", "tooltips", "storage"];

/**
 * Standalone settings page at /settings — the same UI as the settings
 * modal (shared SettingsPanel), laid out as a regular page like
 * /data-source. The gear button keeps opening the modal for quick tweaks;
 * deep links, "open in new tab" and full-page editing live here.
 *
 * Navigation stays inside the SPA: the header links go through navigate()
 * (no full document reload), and section deep links (/settings#tooltips,
 * #priority, …) pre-expand the matching disclosure and scroll to it.
 */
export function SettingsPage(): VNode {
  const { toasts, pushToast } = useToasts();

  // Local mirrors of the persisted settings — the panel mutates them
  // through the callbacks below; mirrors keep the page consistent.
  const [preferred, setPreferred] = useState<string | null>(getPreferredInstance);
  const [redirectX, setRedirectXState] = useState<boolean>(getRedirectX);
  const [customSource, setCustomSourceState] = useState<string>(getCustomSource);

  // Re-read every localStorage-derived mirror — called after per-row
  // resets, tooltip/bias resets and undos, so the page never shows state
  // that no longer exists (or hides state that was just restored).
  const syncMirrors = useCallback(() => {
    setPreferred(getPreferredInstance());
    setRedirectXState(getRedirectX());
    setCustomSourceState(getCustomSource());
  }, []);

  // Deep links: /settings#tooltips / #priority pre-expand their disclosure
  // (openSections, applied on mount and on every hashchange); every known
  // section hash scrolls itself into view.
  const [hash, setHash] = useState(window.location.hash.replace(/^#/, ""));
  useEffect(() => {
    const onHash = () => setHash(window.location.hash.replace(/^#/, ""));
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  const openSections = { priority: hash === "priority", tooltips: hash === "tooltips" };
  useEffect(() => {
    if (!SECTIONS.includes(hash)) return;
    const id = window.requestAnimationFrame(() => {
      document
        .getElementById(`set-${hash}`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => window.cancelAnimationFrame(id);
  }, [hash]);

  return (
    <div class="ds">
      <header class="header">
        <div class="header__inner">
          <a
            class="brand"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate("/");
            }}
          >
            <div class="brand__logo">N</div>
            <div class="brand__name">
              Nitter
              <small class="subtitle">Settings</small>
            </div>
          </a>
          <div class="header__actions">
            <a
              class="btn btn--sm"
              href="/"
              onClick={(e) => {
                e.preventDefault();
                navigate("/");
              }}
            >
              ← Dashboard
            </a>
          </div>
        </div>
      </header>

      {/* "Keep Nitter alive!" funding pledge — sticky under the header. */}
      <DonationBanner />

      <main class="container ds__main set-page__main">
        <h1>Settings</h1>
        <p class="set-page__intro">
          Everything on this page is stored in this browser only. Nothing is ever sent to the server.
        </p>

        <div class="set-page__card">
          <SettingsPanel
            redirectX={redirectX}
            onRedirectX={(on) => {
              setRedirectX(on);
              setRedirectXState(on);
              pushToast({
                kind: "success",
                text: on
                  ? "x.com redirect is ON — /v links now go straight to x.com."
                  : "x.com redirect is OFF — /v links use Nitter instances again.",
              });
            }}
            customSource={customSource}
            onCustomSource={(url) => {
              setCustomSource(url);
              setCustomSourceState(url);
              pushToast({
                kind: "success",
                text: url
                  ? "Custom data source saved for this browser."
                  : "Custom data source removed — the server default is used again.",
              });
            }}
            preferred={preferred}
            onClearPreferred={() => {
              setPreferredInstance(null);
              setPreferred(null);
              pushToast({ kind: "success", text: "Preferred instance cleared." });
            }}
            onToast={pushToast}
            onStorageChanged={syncMirrors}
            openSections={openSections}
          />
        </div>
      </main>

      <ToastHost toasts={toasts} />
    </div>
  );
}
